<template>
  <div class="chart-card">
    <div class="chart-header">
      <h4>Top 10 Users</h4>
      <div class="info-tooltip">
        <span class="info-icon">ℹ️</span>
        <div class="tooltip-content">
          Ranks users by total number of device checkouts. Helps identify power users and usage patterns.
        </div>
      </div>
    </div>
    <div class="user-list">
      <div v-for="(user, index) in topUsers" :key="user.email" class="user-item">
        <span class="user-rank">#{{ index + 1 }}</span>
        <span class="user-email">{{ user.email }}</span>
        <span class="user-count">{{ user.checkouts }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { COLORS } from '@/styles/brand'

const topUsers = ref([])

const props = defineProps({
  devices: Array
})

const calculateTopUsers = () => {
  const userCounts = {}
  props.devices.forEach(device => {
    if (device.lastTakenBy) {
      userCounts[device.lastTakenBy] = (userCounts[device.lastTakenBy] || 0) + 1
    }
  })
  
  topUsers.value = Object.entries(userCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([email, checkouts]) => ({ email, checkouts }))
}

watch(() => props.devices, () => {
  if (props.devices) {
    calculateTopUsers()
  }
}, { immediate: true })
</script>

<style scoped>
.chart-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  border-radius: 16px;
  position: relative;
  overflow: visible;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-header h4 {
  color: v-bind('COLORS.PRIMARY.WHITE');
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.info-tooltip {
  position: relative;
  display: inline-block;
}

.info-icon {
  cursor: help;
  font-size: 1rem;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.info-icon:hover {
  opacity: 1;
}

.tooltip-content {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.95);
  color: v-bind('COLORS.PRIMARY.WHITE');
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  width: 280px;
  z-index: 9999;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.tooltip-content::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 12px;
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.95);
}

.info-tooltip:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.user-item:last-child {
  border-bottom: none;
}

.user-rank {
  color: v-bind('COLORS.PRIMARY.RED');
  font-weight: 700;
  min-width: 30px;
}

.user-email {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  flex: 1;
  margin: 0 12px;
}

.user-count {
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-weight: 600;
  font-size: 0.9rem;
}
</style>